/* ==========================================================================
   Nische cart behaviour — shared by the cart drawer and the cart page.

   A cart "root" is any element carrying `data-nisc-root="<section id>"`:
   #CartDrawer for the drawer (section `cart-drawer`), #NischeCart for the
   page (its own section id). Inside each root, `[data-nisc-swap]` is the box
   whose innerHTML Dawn replaces wholesale on every cart change, and
   `[data-nisc-state]` is the element the Liquid renders the cart's state on.

   Because the contents are swapped wholesale, nothing here may bind a
   listener inside a root: every handler is delegated from `document`, and a
   childList observer per root re-runs the post-render pass whichever code
   path did the swapping (quantity change, add-to-cart, or our own discount
   update). Per-root memory (was the meter unlocked, which state element was
   last seen, the wishlist render token) lives on the root element itself,
   which persists.

   Loaded once by snippets/nische-cart-assets.liquid. Its <script> tag sits
   outside every swap box, so it survives the swaps; scripts inside replaced
   innerHTML never execute anyway.
   ========================================================================== */

(function () {
  if (window.NischeCartDrawer) return;

  var CELEBRATE_MS = 2800;
  var PIECES = 34;
  var CONFETTI = ['#FAC4B4', '#B13E18', '#E3B778', '#9FC9D8', '#F4A08A'];

  var cardCache = {}; /* wishlist card HTML by handle, shared by every root */

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-nisc-root]'));
  }

  function firstRoot() {
    return document.querySelector('[data-nisc-root]');
  }

  function rootOf(el) {
    return el && el.closest ? el.closest('[data-nisc-root]') : null;
  }

  function swapBox(root) {
    return root && root.querySelector('[data-nisc-swap]');
  }

  /* Cart state is read from an element INSIDE the replaced content. Dawn swaps
     the box's innerHTML and never touches its attributes, so state kept up on
     the box goes stale the moment a quantity changes. */
  function state(root) {
    return root && root.querySelector('[data-nisc-state]');
  }

  function mem(root) {
    if (!root.__nisc) {
      root.__nisc = { wasUnlocked: null, pending: false, lastState: null, wishToken: 0 };
    }
    return root.__nisc;
  }

  function sectionId(root) {
    return root.getAttribute('data-nisc-root');
  }

  /* ---------------------------------------------------------------- render */

  function swap(root, html) {
    var current = swapBox(root);
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-nisc-swap]');
    if (!current || !fresh) return false;

    /* Nothing to carry across: every piece of state travels inside the markup. */
    current.innerHTML = fresh.innerHTML;
    return true;
  }

  /* ----------------------------------------------------------- celebration */

  /* Built here rather than in Liquid: it costs nothing until it fires, it can
     cover the whole panel instead of just the meter, and it cannot be left
     behind stale by a re-render. On the page the overlay is fixed to the
     viewport (nische-cart-page.css), so the fall is capped at the viewport. */
  function celebrate(root) {
    root = root || firstRoot();
    var panel = swapBox(root);
    if (!panel) return;

    var existing = panel.querySelector('.nisc-celebrate');
    if (existing) existing.parentNode.removeChild(existing);

    var fall = Math.round(Math.min(panel.getBoundingClientRect().height || 700, window.innerHeight || 700)) + 40;
    var box = document.createElement('div');
    box.className = 'nisc-celebrate';
    box.setAttribute('aria-hidden', 'true');

    var html = '';
    for (var i = 0; i < PIECES; i++) {
      var w = 5 + (i % 4) * 2;
      html +=
        '<i style="' +
        '--x:' + ((i * 37) % 100) + '%;' +
        '--d:' + ((i * 53) % 700) + 'ms;' +
        '--dur:' + (1500 + ((i * 173) % 900)) + 'ms;' +
        '--r:' + (180 + ((i * 97) % 540)) + 'deg;' +
        '--sway:' + (((i % 5) - 2) * 18) + 'px;' +
        '--fall:' + fall + 'px;' +
        '--w:' + w + 'px;' +
        '--h:' + (w + 3 + (i % 3) * 2) + 'px;' +
        '--br:' + (i % 3 === 0 ? '50%' : '1px') + ';' +
        '--c:' + CONFETTI[i % CONFETTI.length] + ';' +
        '"></i>';
    }
    box.innerHTML = html;
    panel.appendChild(box);

    var ship = panel.querySelector('[data-nisc-ship]');
    if (ship) {
      ship.classList.remove('nisc-ship--pop');
      void ship.offsetWidth;
      ship.classList.add('nisc-ship--pop');
    }

    window.setTimeout(function () {
      if (box.parentNode) box.parentNode.removeChild(box);
      if (ship) ship.classList.remove('nisc-ship--pop');
    }, CELEBRATE_MS);
  }

  function checkUnlock(root) {
    var box = state(root);
    if (!box) return;
    var m = mem(root);

    var unlocked = box.getAttribute('data-nisc-unlocked') === 'true';
    var enabled = box.getAttribute('data-nisc-celebrate') === 'true';

    /* The first render of a page only records the state — a cart that was
       already over the threshold must not throw confetti on every page load.
       After that every false -> true transition fires, however it happened:
       a new line, a quantity bump, or a removal that was then undone. */
    if (m.wasUnlocked === null) {
      m.wasUnlocked = unlocked;
      return;
    }

    if (unlocked && !m.wasUnlocked && enabled) celebrate(root);
    m.wasUnlocked = unlocked;
  }

  /* -------------------------------------------------------------- wishlist */

  function syncMoveButtons(root) {
    if (!window.NischeWishlist || !root) return;

    root.querySelectorAll('[data-nisc-move]').forEach(function (btn) {
      btn.classList.toggle('nisc-wish--saved', window.NischeWishlist.has(btn.dataset.niscMove));
    });
  }

  /* The wishlist rail.

     The list lives in the shopper's own localStorage, so Liquid cannot render
     it — each saved handle's card is fetched through
     templates/product.nische-cart-card.liquid, which renders the very same
     card snippet used everywhere else. Responses are cached for the life of
     the page, so re-opening the drawer or changing a quantity costs nothing. */
  function fetchWishCard(handle) {
    if (cardCache[handle] !== undefined) return Promise.resolve(cardCache[handle]);

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(base + 'products/' + encodeURIComponent(handle) + '?view=nische-cart-card')
      .then(function (res) {
        if (!res.ok) throw new Error('not found');
        return res.text();
      })
      .then(function (html) {
        cardCache[handle] = html;
        return html;
      })
      .catch(function () {
        /* Unpublished or deleted since it was saved — forget it rather than
           leaving a hole in the rail on every render. */
        cardCache[handle] = null;
        if (window.NischeWishlist) window.NischeWishlist.remove(handle);
        return null;
      });
  }

  /* Product handles already in the bag, wrapped in commas so a handle can never
     match a substring of a longer one. Rendered by Liquid onto the state
     element, which comes back fresh on every cart change — so this costs no
     request and needs no invalidating. */
  function cartHandles(root) {
    var box = state(root);
    return box ? box.getAttribute('data-nisc-cart-handles') || ',' : ',';
  }

  function renderWishRail(root) {
    var rail = root && root.querySelector('[data-nisc-wishrail]');
    if (!rail) return;
    var m = mem(root);

    var track = rail.querySelector('[data-nisc-wishrail-track]');
    var saved = (window.NischeWishlist && window.NischeWishlist.read()) || [];

    /* Filtered for THIS rail only. Nothing is removed from the wishlist — the
       product stays saved, its heart stays filled everywhere else, and the
       wishlist page still lists it. There is no point offering a piece that is
       already sitting in the bag a few centimetres above. */
    var inCart = cartHandles(root);
    var handles = saved.filter(function (h) {
      return inCart.indexOf(',' + h + ',') === -1;
    });

    if (!handles.length) {
      /* Bump the token here too. Un-hearting the last few pieces fires one
         event per removal, and the earlier renders are still in flight with a
         non-empty list — without this they land after the rail has been hidden
         and put it straight back. */
      m.wishToken++;
      track.innerHTML = '';
      rail.hidden = true;
      return;
    }

    /* Hearts can be tapped faster than the fetches resolve; only the newest
       render is allowed to write. */
    var token = ++m.wishToken;

    Promise.all(handles.map(fetchWishCard)).then(function (cards) {
      if (token !== m.wishToken) return;

      var frag = document.createDocumentFragment();
      var shown = 0;

      cards.forEach(function (html, i) {
        if (!html) return;
        var li = document.createElement('li');
        li.className = 'nisc-rec__card';
        li.setAttribute('data-nisc-wish-item', handles[i]);
        li.innerHTML = html;
        frag.appendChild(li);
        shown++;
      });

      track.innerHTML = '';
      track.appendChild(frag);
      rail.hidden = shown === 0;
      if (window.NischeWishlist) window.NischeWishlist.hydrate(rail);
      root.dispatchEvent(new CustomEvent('nische:cart:render', { bubbles: true }));
    });
  }

  function moveToWishlist(btn) {
    var handle = btn.dataset.niscMove;
    if (!handle) return;

    if (window.NischeWishlist && !window.NischeWishlist.has(handle)) {
      window.NischeWishlist.toggle(handle);
    }

    /* Let Dawn do the removal: clicking its own control keeps the section
       re-render, the focus trap and the line-item events all intact. */
    var row = btn.closest('.nisc-item');
    var remove = row && row.querySelector('cart-remove-button button, nische-cart-remove button');
    if (remove) remove.click();
  }

  /* ---------------------------------------------------------------- coupon */

  function couponBox(root) {
    return root && root.querySelector('[data-nisc-coupon]');
  }

  function setMessage(root, text) {
    var box = couponBox(root);
    var msg = box && box.querySelector('[data-nisc-coupon-msg]');
    if (msg) msg.textContent = text || '';
  }

  function setBusy(root, busy) {
    var box = couponBox(root);
    var apply = box && box.querySelector('[data-nisc-coupon-apply]');
    if (apply) apply.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function liveCodes(root) {
    var box = state(root);
    var raw = box ? box.getAttribute('data-nisc-codes') || '' : '';
    return raw
      .split(',')
      .map(function (c) {
        return c.trim();
      })
      .filter(Boolean);
  }

  /* POST /cart/update.js takes a comma-joined list; an empty string clears
     every code. Asking for the section back in the same request means one
     round trip for apply + repaint. */
  function postDiscount(root, codes, withSection) {
    var body = { discount: codes.join(',') };
    if (withSection) {
      body.sections = [sectionId(root)];
      body.sections_url = window.location.pathname;
    }

    return fetch(window.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  function paint(root, cart) {
    var id = sectionId(root);
    if (cart.sections && cart.sections[id]) swap(root, cart.sections[id]);
    afterRender(root);
  }

  function isApplied(cart, code) {
    var want = code.trim().toUpperCase();

    /* Preferred signal: Shopify reports applicability per code. */
    if (Array.isArray(cart.discount_codes)) {
      var hit = cart.discount_codes.filter(function (d) {
        return (d.code || '').toUpperCase() === want;
      })[0];
      if (hit) return hit.applicable !== false;
    }

    /* Fallbacks for shapes that omit discount_codes: an order-level discount
       shows up by title, a product-level one only moves total_discount. */
    var byTitle = (cart.cart_level_discount_applications || []).some(function (d) {
      return (d.title || '').toUpperCase() === want;
    });

    return byTitle || (cart.total_discount || 0) > 0;
  }

  function applyCoupon(root) {
    var box = couponBox(root);
    var input = box && box.querySelector('[data-nisc-coupon-input]');
    if (!input) return;

    var code = input.value.trim();
    if (!code) {
      setMessage(root, 'Enter a code first.');
      input.focus();
      return;
    }

    var previous = liveCodes(root);
    var next = previous.filter(function (c) {
      return c.toUpperCase() !== code.toUpperCase();
    });
    next.push(code);

    setBusy(root, true);
    setMessage(root, '');

    postDiscount(root, next, true)
      .then(function (cart) {
        if (isApplied(cart, code)) {
          input.value = '';
          paint(root, cart);
          return null;
        }

        /* Rejected. Put the cart back exactly as it was and say so — an
           inapplicable code changes no totals, so nothing needs repainting. */
        setMessage(root, 'That code is not valid for this bag.');
        return postDiscount(root, previous, false);
      })
      .catch(function () {
        setMessage(root, 'Could not apply that code. Please try again.');
      })
      .finally(function () {
        setBusy(root, false);
      });
  }

  function removeCoupon(root, code) {
    var next = liveCodes(root).filter(function (c) {
      return c.toUpperCase() !== code.trim().toUpperCase();
    });

    setBusy(root, true);
    postDiscount(root, next, true)
      .then(function (cart) {
        paint(root, cart);
      })
      .catch(function () {
        setMessage(root, 'Could not remove that code.');
      })
      .finally(function () {
        setBusy(root, false);
      });
  }

  /* ------------------------------------------------------------ delegation */

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var root = rootOf(t);
    if (!root) return;

    var move = t.closest('[data-nisc-move]');
    if (move) {
      event.preventDefault();
      moveToWishlist(move);
      return;
    }

    var toggle = t.closest('[data-nisc-coupon-toggle]');
    if (toggle) {
      var box = toggle.closest('[data-nisc-coupon]');
      var open = box.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        var field = box.querySelector('[data-nisc-coupon-input]');
        if (field) field.focus();
      }
      return;
    }

    var apply = t.closest('[data-nisc-coupon-apply]');
    if (apply) {
      event.preventDefault();
      applyCoupon(root);
      return;
    }

    var drop = t.closest('[data-nisc-code-remove]');
    if (drop) {
      event.preventDefault();
      removeCoupon(root, drop.getAttribute('data-nisc-code-remove'));
    }
  });

  /* Enter inside the code field must not submit the cart form to checkout. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (!event.target.closest) return;
    if (!event.target.closest('[data-nisc-coupon-input]')) return;
    event.preventDefault();
    applyCoupon(rootOf(event.target));
  });

  /* ---------------------------------------------------------- render hooks */

  function afterRender(root) {
    root = root || firstRoot();
    if (!root) return;
    mem(root).lastState = state(root);
    if (window.NischeWishlist) window.NischeWishlist.hydrate(root);
    syncMoveButtons(root);
    renderWishRail(root);
    checkUnlock(root);
    /* Lets the page script wire freshly rendered product forms. */
    root.dispatchEvent(new CustomEvent('nische:cart:render', { bubbles: true }));
  }

  /* The observer has to tell "Dawn re-rendered the cart" apart from "we just
     wrote into it ourselves" — otherwise filling the wishlist rail, or dropping
     the confetti in, re-triggers the pass that did it. A re-render always
     creates a NEW [data-nisc-state] element; our own writes never do. */
  function schedule(root) {
    var m = mem(root);
    if (m.pending) return;
    m.pending = true;
    window.requestAnimationFrame(function () {
      m.pending = false;
      if (state(root) === m.lastState) return;
      afterRender(root);
    });
  }

  function watch(root) {
    afterRender(root);

    /* childList only: afterRender toggles classes, so watching attributes
       would make it re-enter itself. */
    new MutationObserver(function () {
      schedule(root);
    }).observe(root, { childList: true, subtree: true });
  }

  function start() {
    var all = roots();
    if (!all.length) return;
    all.forEach(watch);

    /* A heart toggled anywhere on the page — a card in the grid, a card in the
       cart, the "Move to wishlist" link — lands here. */
    document.addEventListener('nische:wishlist:change', function () {
      roots().forEach(function (root) {
        syncMoveButtons(root);
        renderWishRail(root);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.NischeCartDrawer = {
    celebrate: celebrate,
    refresh: afterRender,
    wishlist: renderWishRail,
    swap: swap,
    roots: roots,
  };
})();
